import React, { useEffect, useState } from "react";
import { Clock, ChevronLeft, ChevronRight, X } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import Spacing from "../../Spacing";
import SectionHeadingStyle3 from "../../SectionHeading/SectionHeadingStyle3";
import Common from "../../../helpers/common";
import AppConstant, {
  BookingPaymentMethodEnum,
  BookingTypeEnum,
  MatchTypeEnum,
  PaymentTypeEnum,
  PromoCodeDiscountTypeEnum,
  respEnum,
  TransactionLogTypeEnum,
  PaymentStatusEnum,
  TermsnConditionTypeEnum,
  BookingModeEnum,
  isTestingPayment,
} from "../../../constants/appConstants";
import {
  getSports,
  getAvailableTimeSlots,
  getSelectedDate,
  getSelectedSport,
  getSelectedCourt,
  getSelectedDuration,
  getSelectedTime,
  setSports,
  setTimeSlots,
  setSelectedDate,
  setSelectedSport,
  setSelectedCourt,
  setSelectedDuration,
  setSelectedTime,
  setOffPeakRates,
  getOffPeakRates,
  setCourtCharges,
  getCourtCharges,
  setClubAccounts,
  getClubAccounts,
  setSelectedPaymentTypeId,
  getSelectedPaymentTypeId,
  setPromoCode,
  setPromoCodeError,
  setPromoCodeDetails,
  setDiscount,
  getPromoCode,
  getPromoCodeError,
  getPromoCodeDetails,
  getSubTotal,
  getDiscount,
  setAdvancePayment,
  setPayOnClub,
  setPartialPaymentPercentage,
  getAdvancePayment,
  getPayOnClub,
  getPartialPaymentPercentage,
  setBookingPayload,
  setTermsAndConditions,
  getTermsAndConditions,
  setIsBookingFlow,
  getIsBookingFlow,
  setClubDiscount,
  getClubDiscount,
  setOriginalCourtCharges,
  getOriginalCourtCharges,
  setBankDiscounts,
  getBankDiscounts,
} from "../../../store/bookingSlice";
import Utility from "../../../helpers/utilityHelper";
import { Button } from "bootstrap";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { getUser } from "../../../helpers/sessionHelper";
import LoginModal from "../../LoginModal";
import {
  sanitizeInput,
  checkRateLimit,
  validateBookingPayload,
  logSecurityEvent,
} from "../../../helpers/securityHelper";

// Set page title and favicon

const SportBooking = () => {
  // Redux Dispatch
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux selectors
  const sports = useSelector(getSports);
  const availableTimeSlots = useSelector(getAvailableTimeSlots);
  const selectedDate = useSelector(getSelectedDate);
  const selectedSport = useSelector(getSelectedSport);
  const selectedCourt = useSelector(getSelectedCourt);
  const selectedDuration = useSelector(getSelectedDuration);
  const selectedTime = useSelector(getSelectedTime);
  const offPeakRates = useSelector(getOffPeakRates);
  const courtCharges = useSelector(getCourtCharges);
  const originalCourtCharges = useSelector(getOriginalCourtCharges);
  const clubDiscount = useSelector(getClubDiscount);
  const clubAccounts = useSelector(getClubAccounts);
  const selectedPaymentTypeId = useSelector(getSelectedPaymentTypeId);
  const promoCode = useSelector(getPromoCode);
  const promoCodeError = useSelector(getPromoCodeError);
  const promoCodeDetails = useSelector(getPromoCodeDetails);
  const discount = useSelector(getDiscount);
  const subTotal = useSelector(getSubTotal);
  const advancePayment = useSelector(getAdvancePayment);
  const payOnClub = useSelector(getPayOnClub);
  const partialPaymentPercentage = useSelector(getPartialPaymentPercentage);
  const termsAndConditions = useSelector(getTermsAndConditions);
  const isBookingFlow = useSelector(getIsBookingFlow);
  const bankDiscounts = useSelector((state) => state.booking.bankDiscounts || []);

  // Local state
  const [isProcessingCheckout, setIsProcessingCheckout] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showBookingTermsModal, setShowBookingTermsModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showBookingSuccessModal, setShowBookingSuccessModal] = useState(false);
  const [bookingSuccessData, setBookingSuccessData] = useState(null);
  const [showPadelLoader, setShowPadelLoader] = useState(false);
  const [loaderMessage, setLoaderMessage] = useState("Loading...");
  const [isBookingInProgress, setIsBookingInProgress] = useState(false);
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const [selectedBank, setSelectedBank] = useState(null);
  const [selectedCardOffer, setSelectedCardOffer] = useState(null);

  // Helper function to get banks data from Redux
  const getBanksData = () => {
    if (!bankDiscounts || bankDiscounts.length === 0) {
      return [];
    }
    return bankDiscounts.map((bank) => ({
      id: `bank-${bank.bankId}`,
      name: bank.bankName,
      logo: bank.bankIcon || "/images/bank.png",
      bankId: bank.bankId,
      bankColorCode: bank.bankColorCode,
    }));
  };

  // Helper function to get card offers by bank name from Redux
  const getCardOffersByBank = (bankName) => {
    if (!bankDiscounts || bankDiscounts.length === 0 || !bankName) {
      return [];
    }
    const bank = bankDiscounts.find((b) => b.bankName === bankName);
    if (!bank || !bank.discounts || bank.discounts.length === 0) {
      return [];
    }
    return bank.discounts.map((discount) => ({
      name: discount.name,
      bin: discount.bin,
      cap: discount.maxDiscount?.toString() || "0",
      discount: discount.discountValue?.toString() || "0",
      id: discount.id,
      discountType: discount.discountType,
      transactionallowedperday: discount.transactionallowedperday,
      minOrderAmount: discount.minOrderAmount,
      validfrom: discount.validfrom,
      validtill: discount.validtill,
    }));
  };

  // Set initial selected bank from Redux data
  useEffect(() => {
    if (bankDiscounts && bankDiscounts.length > 0 && !selectedBank) {
      setSelectedBank(bankDiscounts[0].bankName);
    }
  }, [bankDiscounts, selectedBank]);

  useEffect(() => {
    if (selectedTime && selectedDuration) {
      updateSubTotal();
    }
    // Remove promo code if club discount becomes active
    if (clubDiscount > 0 && promoCodeDetails) {
      handleRemovePromoCode();
    }
  }, [selectedTime, selectedDuration, selectedCourt, clubDiscount]);

  useEffect(() => {
    if (!sports.length) {
      getData();
    }
  }, []);

  useEffect(() => {
    if (selectedCourt && selectedDuration) {
      getTimeSlots(selectedCourt, selectedDuration, selectedDate);
    }
  }, [selectedDate, selectedCourt, selectedDuration]);

  useEffect(() => {
    if (clubAccounts?.length > 0 && !selectedPaymentTypeId) {
      const firstActiveOption = clubAccounts.find((option) => option.isActive);
      if (firstActiveOption) {
        dispatch(setSelectedPaymentTypeId(firstActiveOption.paymentTypeId));
      }
    }
  }, [clubAccounts]);

  useEffect(() => {
    if (!selectedTime) {
      handleRemovePromoCode();
    }
  }, [selectedTime]);
  useEffect(() => {
    document.title = "Bookings | Ignite";
  }, []);

  // Check login status on component mount and after login
  useEffect(() => {
    const checkLoginStatus = () => {
      const user = getUser();
      setIsUserLoggedIn(!!user);
    };

    checkLoginStatus();

    // Check login status periodically in case user logs out in another tab
    const interval = setInterval(checkLoginStatus, 5000);

    return () => clearInterval(interval);
  }, []);
  // Handlers
  const handleSportSelect = (sportLabel) => {
    dispatch(setSelectedSport(sportLabel));
    dispatch(setSelectedCourt(""));
    dispatch(setSelectedDuration(null));
    dispatch(setSelectedTime(""));

    const updatedSports = sports.map((sport) => ({
      ...sport,
      isSelected: sport.label === sportLabel,
      options: sport.options.map((court) => ({
        ...court,
        isSelected: false,
      })),
    }));
    dispatch(setSports(updatedSports));
  };

  const handleCourtSelect = (courtId) => {
    dispatch(setSelectedCourt(courtId));
    dispatch(setSelectedTime(""));

    const sport = sports.find((s) => s.label === selectedSport);
    const court = sport?.options.find((c) => c.value === courtId);

    // Set default duration
    const format60Min = court?.formats.find((f) => f.value === 60);
    const firstFormat = court?.formats[0];
    const defaultDuration = format60Min ? 60 : firstFormat?.value;

    dispatch(setSelectedDuration(defaultDuration));

    const updatedSports = sports.map((sport) => ({
      ...sport,
      options: sport.options.map((court) => ({
        ...court,
        isSelected: court.value === courtId,
      })),
    }));
    dispatch(setSports(updatedSports));

    // Fetch time slots after court selection
    if (defaultDuration) {
      getTimeSlots(courtId, defaultDuration, selectedDate);
    }
  };

  const handleDurationChange = (duration) => {
    dispatch(setSelectedDuration(Number(duration)));
    dispatch(setSelectedTime(""));
    handleRemovePromoCode();
    getTimeSlots(selectedCourt, Number(duration), selectedDate);
    updateSubTotal();
  };

  const handleTimeSelect = (time) => {
    if (time !== selectedTime) {
      handleRemovePromoCode();
    }
    dispatch(setSelectedTime(time));
    updateSubTotal();
  };

  const getSelectedCourtFormats = () => {
    if (!selectedSport || !selectedCourt) return [];
    const sport = sports.find((s) => s.label === selectedSport);
    if (!sport) return [];
    const court = sport.options.find((c) => c.value === selectedCourt);
    return court ? court.formats : [];
  };

  const getOverlapMinutes = (start1, end1, start2, end2) => {
    const parseTime = (timeStr) => {
      const [hours, minutes] = timeStr.split(":").map(Number);
      return hours * 60 + minutes;
    };

    const start1Mins = parseTime(start1);
    const end1Mins = parseTime(end1);
    const start2Mins = parseTime(start2);
    const end2Mins = parseTime(end2);

    const overlapStart = Math.max(start1Mins, start2Mins);
    const overlapEnd = Math.min(end1Mins, end2Mins);

    return Math.max(0, overlapEnd - overlapStart);
  };

  const getCourtPrice = () => {
    if (!selectedDuration || !selectedTime) {
      return 0;
    }

    const formats = getSelectedCourtFormats();
    const selectedFormat = formats.find((f) => f.value === selectedDuration);
    if (!selectedFormat) {
      return 0;
    }

    const standardRate = selectedFormat.charges;

    const sport = sports.find((s) => s.label === selectedSport);
    if (!sport) {
      // Apply club discount if available
      const originalPrice = standardRate;
      dispatch(setOriginalCourtCharges(originalPrice));

      if (clubDiscount > 0) {
        const discountedPrice = Math.round(
          originalPrice * (1 - clubDiscount / 100)
        );
        return discountedPrice;
      }
      return originalPrice;
    }

    const [startHours, startMinutes] = selectedTime.split(":");
    const bookingStart = new Date();
    bookingStart.setHours(parseInt(startHours), parseInt(startMinutes), 0);

    const bookingEnd = new Date(
      bookingStart.getTime() + selectedDuration * 60000
    );
    const bookingEndTime = `${bookingEnd
      .getHours()
      .toString()
      .padStart(2, "0")}:${bookingEnd
        .getMinutes()
        .toString()
        .padStart(2, "0")}:00`;

    // Find applicable off-peak rate
    const applicableOffPeak = offPeakRates.find(
      (rate) => rate.sportId === sport.id
    );
    if (!applicableOffPeak) {
      // Apply club discount if available
      const originalPrice = standardRate;
      dispatch(setOriginalCourtCharges(originalPrice));

      if (clubDiscount > 0) {
        const discountedPrice = Math.round(
          originalPrice * (1 - clubDiscount / 100)
        );
        return discountedPrice;
      }
      return originalPrice;
    }

    // Calculate minutes in different rate periods
    const offPeakMinutes = getOverlapMinutes(
      selectedTime,
      bookingEndTime,
      applicableOffPeak.start,
      applicableOffPeak.end
    );

    const peakMinutes = selectedDuration - offPeakMinutes;

    // Calculate rates
    const standardPerMinute = standardRate / selectedDuration;
    const offPeakPerMinute = applicableOffPeak.amount / 60;

    // Calculate total
    const offPeakCost = offPeakMinutes * offPeakPerMinute;
    const peakCost = peakMinutes * standardPerMinute;
    const originalPrice = Math.round(offPeakCost + peakCost);

    // Store original price
    dispatch(setOriginalCourtCharges(originalPrice));

    // Apply club discount if available
    if (clubDiscount > 0) {
      const discountedPrice = Math.round(
        originalPrice * (1 - clubDiscount / 100)
      );
      return discountedPrice;
    }

    return originalPrice;
  };

  const updateSubTotal = () => {
    const totalCost = getCourtPrice();
    dispatch(setCourtCharges(totalCost));
  };

  const calculateEndTime = (startTime, durationMinutes) => {
    const [hours, minutes] = startTime.split(":");
    const startDate = new Date();
    startDate.setHours(parseInt(hours), parseInt(minutes), 0);

    const endDate = new Date(startDate.getTime() + durationMinutes * 60000);
    const endHours = endDate.getHours();
    const endMinutes = endDate.getMinutes();

    const endTimeStr = `${endHours.toString().padStart(2, "0")}:${endMinutes
      .toString()
      .padStart(2, "0")}`;

    return `${Utility.formatTimeToAMPM(startTime)} - ${Utility.formatTimeToAMPM(
      endTimeStr
    )}`;
  };

  const handleDateChange = (increment) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + increment);

    // Get current date with time set to midnight for proper comparison
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Get max date (3 months from today)
    const maxDate = new Date(today);
    maxDate.setMonth(maxDate.getMonth() + 3);

    // Only allow date change if the new date is today or in the future and within 3 months
    if (newDate >= today && newDate <= maxDate) {
      // Format the date in ISO format to avoid timezone issues
      const year = newDate.getFullYear();
      const month = String(newDate.getMonth() + 1).padStart(2, "0");
      const day = String(newDate.getDate()).padStart(2, "0");
      const formattedNewDate = `${year}-${month}-${day}T00:00:00.000Z`;

      dispatch(setSelectedDate(formattedNewDate));
      dispatch(setSelectedTime("")); // Clear selected time when date changes

      // Fetch new time slots if court and duration are selected
      if (selectedCourt && selectedDuration) {
        getTimeSlots(selectedCourt, selectedDuration, formattedNewDate);
      }
    }
  };

  const handleCalendarDateChange = (event) => {
    const selectedCalendarDate = event.target.value;
    if (!selectedCalendarDate) return;

    const newDate = new Date(selectedCalendarDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Get max date (3 months from today)
    const maxDate = new Date(today);
    maxDate.setMonth(maxDate.getMonth() + 3);

    // Only allow date change if the new date is today or in the future and within 3 months
    if (newDate >= today && newDate <= maxDate) {
      // Format the date in ISO format to avoid timezone issues
      const year = newDate.getFullYear();
      const month = String(newDate.getMonth() + 1).padStart(2, "0");
      const day = String(newDate.getDate()).padStart(2, "0");
      const formattedNewDate = `${year}-${month}-${day}T00:00:00.000Z`;

      dispatch(setSelectedDate(formattedNewDate));
      dispatch(setSelectedTime("")); // Clear selected time when date changes

      // Fetch new time slots if court and duration are selected
      if (selectedCourt && selectedDuration) {
        getTimeSlots(selectedCourt, selectedDuration, formattedNewDate);
      }
    }
  };

  const getMinDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getMaxDate = () => {
    const today = new Date();
    const maxDate = new Date(today);
    maxDate.setMonth(maxDate.getMonth() + 3);
    const year = maxDate.getFullYear();
    const month = String(maxDate.getMonth() + 1).padStart(2, "0");
    const day = String(maxDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getCurrentDateValue = () => {
    const date = new Date(selectedDate);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const isTimeBookable = (slotTime) => {
    const now = new Date();
    const [slotHours, slotMinutes] = slotTime.split(":");
    const slotDate = new Date(selectedDate);
    slotDate.setHours(parseInt(slotHours), parseInt(slotMinutes), 0);

    return slotDate > now;
  };

  const InitPaymentOptions = (accounts) => {
    const paymentOptions = [];
    debugger;
    if (accounts) {
      accounts.forEach((item) => {
        if (
          item["paymentTypeId"] === PaymentTypeEnum.Pay_On_Club &&
          item["accounts"] &&
          item["accounts"].length > 0
        ) {
          paymentOptions.push({
            ...item["accounts"][0],
            display: item["accounts"][0]["name"],
            icon: item.paymentTypeIcon,
            paymentTypeId: item["paymentTypeId"],
          });
        } else if (
          item["paymentTypeId"] === PaymentTypeEnum.Payment_GateWay &&
          item["accounts"] &&
          item["accounts"].length > 0
        ) {
          paymentOptions.push({
            ...item["accounts"][0],
            display: item["accounts"][0]["paymentGateway"],
            icon: item.paymentTypeIcon,
            paymentTypeId: item["paymentTypeId"],
          });
        } else if (
          item["paymentTypeId"] === PaymentTypeEnum.Online_Partial_Pay &&
          item["accounts"] &&
          item["accounts"].length > 0
        ) {
          paymentOptions.push({
            ...item["accounts"][0],
            display: `Partial (${item["accounts"][0]["percentage"]}%)`,
            icon: item.paymentTypeIcon,
            paymentTypeId: item["paymentTypeId"],
            percentage: item["accounts"][0]["percentage"],
          });
        }
      });
    }
    const indexMap = {
      [PaymentTypeEnum.Payment_GateWay]: 0,
      [PaymentTypeEnum.Online_Partial_Pay]: 1,
      [PaymentTypeEnum.Pay_On_Club]: 2,
      [PaymentTypeEnum.Online]: 3,
    };
    if (paymentOptions.length > 0) {
      paymentOptions.sort((a, b) => {
        const idA = a["paymentTypeId"];
        const idB = b["paymentTypeId"];
        const indexA = indexMap.hasOwnProperty(idA)
          ? indexMap[idA]
          : Number.MAX_SAFE_INTEGER; // Fallback for extra elements
        const indexB = indexMap.hasOwnProperty(idB)
          ? indexMap[idB]
          : Number.MAX_SAFE_INTEGER;
        return indexA - indexB;
      });

      return paymentOptions;
    }
    return [];
  };

  const calculatePartialPayments = (paymentTypeId, selectedAccount) => {
    if (
      paymentTypeId === PaymentTypeEnum.Online_Partial_Pay &&
      selectedAccount?.percentage
    ) {
      const percentage = selectedAccount.percentage;
      dispatch(setPartialPaymentPercentage(percentage));

      // Calculate advance and pay on club amounts
      const advance = Math.floor(subTotal * (percentage / 100));
      const payOnClubAmount = subTotal - advance;

      dispatch(setAdvancePayment(advance));
      dispatch(setPayOnClub(payOnClubAmount));
    } else {
      // Reset partial payment values for other payment types
      dispatch(setPartialPaymentPercentage(0));
      dispatch(setAdvancePayment(0));
      dispatch(setPayOnClub(0));
    }
  };

  const handlePaymentSelect = (paymentTypeId) => {
    dispatch(setSelectedPaymentTypeId(paymentTypeId));
    const selectedAccount = clubAccounts.find(
      (account) => account.paymentTypeId === paymentTypeId
    );
    calculatePartialPayments(paymentTypeId, selectedAccount);
  };

  const handleRemovePromoCode = () => {
    dispatch(setPromoCode(""));
    dispatch(setPromoCodeDetails(null));
    dispatch(setPromoCodeError(""));
    dispatch(setDiscount(0));
    updateSubTotal();
  };

  const handlePromoCodeValidation = async () => {
    // Check if club discount is active
    if (clubDiscount > 0) {
      await Swal.fire({
        icon: "warning",
        title: "Club Discount Active",
        text: "Promo codes cannot be applied when club discount is active. You are already getting the best available discount!",
      });
      return;
    }

    // Input validation and sanitization using security helper
    const sanitizedPromoCode = sanitizeInput(promoCode, "promo_code");

    if (!sanitizedPromoCode) {
      logSecurityEvent("invalid_promo_code", { input: promoCode });
      await Swal.fire({
        icon: "warning",
        title: "Warning",
        text: "Please enter a valid promo code",
      });
      return;
    }

    if (sanitizedPromoCode.length > 50) {
      await Swal.fire({
        icon: "warning",
        title: "Warning",
        text: "Promo code is too long",
      });
      return;
    }

    // Get sport ID from selected sport
    const selectedSportObj = sports.find(s => s.label === selectedSport);
    const sportId = selectedSportObj?.id || null;

    // Create start and end datetime for booking validation
    let startDateTime = null;
    let endDateTime = null;

    if (selectedTime && selectedDate && selectedDuration) {
      const [startHours, startMinutes] = selectedTime.split(':');

      // Format date parts
      let dateObj;
      if (typeof selectedDate === 'string' && selectedDate.includes('T')) {
        dateObj = new Date(selectedDate);
      } else {
        dateObj = new Date(selectedDate);
      }

      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;

      // Create time strings
      const startTimeStr = `${startHours}:${startMinutes}:00`;
      const endHour = Math.floor((parseInt(startHours) * 60 + parseInt(startMinutes) + selectedDuration) / 60);
      const endMinute = (parseInt(startHours) * 60 + parseInt(startMinutes) + selectedDuration) % 60;

      // Handle 24:00:00 case by converting to 23:59:00
      let finalEndHour = endHour;
      let finalEndMinute = endMinute;

      if (endHour === 24 && endMinute === 0) {
        finalEndHour = 23;
        finalEndMinute = 59;
      }

      const endTimeStr = `${String(finalEndHour).padStart(2, '0')}:${String(finalEndMinute).padStart(2, '0')}:00`;

      // Format as yyyy-MM-ddTHH:mm:ss format
      startDateTime = `${formattedDate}T${startTimeStr}`;
      endDateTime = `${formattedDate}T${endTimeStr}`;
    }

    let data = {
      code: sanitizedPromoCode,
      orderAmount: originalCourtCharges > 0 ? originalCourtCharges : courtCharges,
      sportId: sportId,
      startDateTime: startDateTime,
      endDateTime: endDateTime,
      ignoreHolding: false
    };

    try {
      const promoResponse = await Common.ApiService.getInstance().request(
        "ValidatePromoCode",
        data,
        "POST",
        true,
        true,
        true
      );

      if (promoResponse.status === respEnum.Success) {
        const promoData = promoResponse.data[0];
        dispatch(setPromoCodeDetails(promoData));

        if (promoData.discountType == PromoCodeDiscountTypeEnum.Fixed_Amount) {
          dispatch(setDiscount(promoData.discountValue));
        } else if (
          promoData.discountType == PromoCodeDiscountTypeEnum.Percentage
        ) {
          const baseAmount =
            originalCourtCharges > 0 ? originalCourtCharges : courtCharges;
          let discountValue = baseAmount * (promoData.discountValue / 100);
          if (promoData.maxDiscount && discountValue > promoData.maxDiscount) {
            discountValue = promoData.maxDiscount;
          }
          dispatch(setDiscount(discountValue));
        }
        updateSubTotal();
        dispatch(setPromoCodeError(""));
      } else {
        await Swal.fire({
          icon: "error",
          title: "Error",
          text: promoResponse.message || "Invalid promo code",
        });
        dispatch(
          setPromoCodeError(promoResponse.message || "Invalid promo code")
        );
        dispatch(setDiscount(0));
        updateSubTotal();
      }
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "Error validating promo code",
      });
      dispatch(setPromoCodeError("Error validating promo code"));
      dispatch(setDiscount(0));
      updateSubTotal();
    }
  };

  // Booking Success Modal Component
  // Padel and Ball Loader Component
  const PadelLoader = ({ show, message = "Loading..." }) => {
    if (!show) return null;

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
          backdropFilter: "blur(5px)",
        }}
      >
        <div
          style={{
            background: "rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(20px)",
            borderRadius: "20px",
            padding: "40px",
            border: "1px solid rgba(0, 243, 255, 0.3)",
            boxShadow: "0 25px 50px rgba(0, 243, 255, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
            textAlign: "center",
            maxWidth: "350px",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Glass effect overlay */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "1px",
              background: "linear-gradient(90deg, transparent, rgba(0, 243, 255, 0.8), transparent)",
            }}
          />

          {/* Glass Loader */}
          <div
            style={{
              width: "80px",
              height: "80px",
              margin: "0 auto 25px auto",
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {/* Outer ring */}
            <div
              style={{
                position: "absolute",
                width: "40px",
                height: "40px",
                border: "3px solid rgba(0, 243, 255, 0.2)",
                borderRadius: "50%",
                borderTop: "3px solid #00f3ff",
                animation: "spin 1.5s linear infinite",
              }}
            />
            {/* Inner ring */}
            <div
              style={{
                position: "absolute",
                width: "60px",
                height: "60px",
                border: "2px solid rgba(0, 243, 255, 0.1)",
                borderRadius: "50%",
                borderTop: "2px solid #00f3ff",
                animation: "spin 1s linear infinite reverse",
              }}
            />
          </div>

          <h4 style={{
            color: "#00f3ff",
            marginBottom: "10px",
            textShadow: "0 0 10px rgba(0, 243, 255, 0.5)",
            fontWeight: "600"
          }}>
            {message}
          </h4>
          <p style={{
            color: "rgba(255, 255, 255, 0.8)",
            fontSize: "14px",
            margin: 0,
            textShadow: "0 1px 2px rgba(0, 0, 0, 0.3)"
          }}>
            Please wait while we process your request...
          </p>
        </div>

        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @keyframes pulse {
            0%, 100% { 
              transform: scale(1);
              opacity: 1;
            }
            50% { 
              transform: scale(1.2);
              opacity: 0.8;
            }
          }
        `}</style>
      </div>
    );
  };

  const BookingSuccessModal = ({ show, onClose, bookingData }) => {
    if (!show || !bookingData) return null;

    const formatDateTime = (dateTimeString) => {
      if (!dateTimeString) return "-";
      try {
        const date = new Date(dateTimeString);
        return date.toLocaleDateString("en-US", {
          weekday: "short",
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      } catch (error) {
        return dateTimeString;
      }
    };

    const formatTime = (startDateTime, endDateTime) => {
      if (!startDateTime || !endDateTime) return "-";
      try {
        const start = new Date(startDateTime);
        const end = new Date(endDateTime);
        const startTime = start.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
        const endTime = end.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
        return `${startTime} - ${endTime}`;
      } catch (error) {
        return "-";
      }
    };

    const getPaymentModeDisplay = (paymentTypeId) => {
      if (
        paymentTypeId === PaymentTypeEnum.Pay_On_Club ||
        paymentTypeId === "Pay_On_Club"
      ) {
        return "Pay On Club";
      } else if (
        paymentTypeId === PaymentTypeEnum.Payment_GateWay ||
        paymentTypeId === "Payment_GateWay"
      ) {
        return "PayFast";
      } else if (
        paymentTypeId === PaymentTypeEnum.Online_Partial_Pay ||
        paymentTypeId === "Online_Partial_Pay"
      ) {
        return "Partial Payment";
      }
      return "Online Payment";
    };

    const getPaymentModeIcon = (paymentTypeId) => {
      if (
        paymentTypeId === PaymentTypeEnum.Pay_On_Club ||
        paymentTypeId === "Pay_On_Club"
      ) {
        return "🏪";
      } else if (
        paymentTypeId === PaymentTypeEnum.Payment_GateWay ||
        paymentTypeId === "Payment_GateWay"
      ) {
        return "💳";
      } else if (
        paymentTypeId === PaymentTypeEnum.Online_Partial_Pay ||
        paymentTypeId === "Online_Partial_Pay"
      ) {
        return "💰";
      }
      return "💳";
    };

    return (
      <div
        className="modal fade show"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "rgba(0,0,0,0.8)",
          backdropFilter: "blur(5px)",
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: 1050,
        }}
        onClick={onClose}
      >
        <div
          className="modal-dialog modal-content-cus-sm"
          style={{
            marginTop: "700px!important",
            maxWidth: "450px",
            width: "90%",
            margin: "0",
            marginBottom: "50px",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="modal-content modal-content-cus-sm"
            style={{
              backgroundColor: "#1a1a1a",
              border: "2px solid #28a745",
              borderRadius: "15px",
              boxShadow: "0 20px 40px rgba(40, 167, 69, 0.3)",
            }}
          >
            {/* Header */}
            <div
              className="modal-header text-center"
              style={{
                backgroundColor: "#28a745",
                borderBottom: "none",
                borderRadius: "13px 13px 0 0",
                padding: "20px 15px",
              }}
            >
              <div className="w-100 text-center">
                <div style={{ fontSize: "2.5rem", marginBottom: "8px" }}>
                  🎉
                </div>
                <h3
                  className="modal-title text-white mb-0"
                  style={{ fontWeight: "bold", fontSize: "1.5rem" }}
                >
                  Booking Confirmed!
                </h3>
                <p className="text-white-50 mb-0" style={{ fontSize: "14px" }}>
                  Your court has been successfully booked
                </p>
              </div>
              {/* Commented out close button - users must click "View My Bookings" */}
              {/* <button
                type="button"
                className="btn-close btn-close-white position-absolute top-0 end-0 m-3"
                onClick={onClose}
                style={{ fontSize: '20px' }}
              ></button> */}
            </div>

            {/* Body */}
            <div
              className="modal-body"
              style={{ padding: "15px 12px", color: "#ffffff" }}
            >
              {/* Booking Reference */}
              <div className="text-center mb-2">
                <div
                  style={{
                    backgroundColor: "#2a2a2a",
                    padding: "10px",
                    borderRadius: "6px",
                    border: "1px solid #3d3d3d",
                  }}
                >
                  <small style={{ color: "#aaa" }}>BOOKING REFERENCE</small>
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: "bold",
                      color: "#28a745",
                      marginTop: "3px",
                    }}
                  >
                    #{bookingData.refNo || bookingData.id || "N/A"}
                  </div>
                </div>
              </div>

              {/* Booking Details Grid */}
              <div className="row g-1">
                {/* Court & Sport Info */}
                <div className="col-md-6">
                  <div
                    style={{
                      backgroundColor: "#2a2a2a",
                      padding: "12px",
                      borderRadius: "6px",
                      border: "1px solid #3d3d3d",
                      height: "100%",
                    }}
                  >
                    <div className="d-flex align-items-center mb-1">
                      <span style={{ fontSize: "14px", marginRight: "6px" }}>
                        🏟️
                      </span>
                      <h6 className="mb-0" style={{ color: "#28a745" }}>
                        Court Details
                      </h6>
                    </div>
                    <div className="mb-1">
                      <small style={{ color: "#aaa" }}>Court</small>
                      <div style={{ fontWeight: "600" }}>
                        {bookingData.courtName || "N/A"}
                      </div>
                    </div>
                    <div>
                      <small style={{ color: "#aaa" }}>Sport</small>
                      <div style={{ fontWeight: "600" }}>
                        {bookingData.sportName || "N/A"}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Date & Time Info */}
                <div className="col-md-6">
                  <div
                    style={{
                      backgroundColor: "#2a2a2a",
                      padding: "12px",
                      borderRadius: "6px",
                      border: "1px solid #3d3d3d",
                      height: "100%",
                    }}
                  >
                    <div className="d-flex align-items-center mb-1">
                      <span style={{ fontSize: "14px", marginRight: "6px" }}>
                        📅
                      </span>
                      <h6 className="mb-0" style={{ color: "#28a745" }}>
                        Schedule
                      </h6>
                    </div>
                    <div className="mb-1">
                      <small style={{ color: "#aaa" }}>Date</small>
                      <div style={{ fontWeight: "600" }}>
                        {formatDateTime(bookingData.startDateTime)}
                      </div>
                    </div>
                    <div>
                      <small style={{ color: "#aaa" }}>Time Slot</small>
                      <div style={{ fontWeight: "600" }}>
                        {formatTime(
                          bookingData.startDateTime,
                          bookingData.endDateTime
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment Info */}
                <div className="col-md-6">
                  <div
                    style={{
                      backgroundColor: "#2a2a2a",
                      padding: "12px",
                      borderRadius: "6px",
                      border: "1px solid #3d3d3d",
                      height: "100%",
                    }}
                  >
                    <div className="d-flex align-items-center mb-1">
                      <span style={{ fontSize: "14px", marginRight: "6px" }}>
                        {getPaymentModeIcon(bookingData.paymentTypeId)}
                      </span>
                      <h6 className="mb-0" style={{ color: "#28a745" }}>
                        Payment Details
                      </h6>
                    </div>
                    <div className="mb-1">
                      <small style={{ color: "#aaa" }}>Payment Mode</small>
                      <div style={{ fontWeight: "600" }}>
                        {getPaymentModeDisplay(bookingData.paymentTypeId)}
                      </div>
                    </div>
                    <div>
                      <small style={{ color: "#aaa" }}>Status</small>
                      <div>
                        <span
                          style={{
                            backgroundColor: bookingData.isPaid
                              ? "#28a745"
                              : "#ffc107",
                            color: bookingData.isPaid ? "white" : "black",
                            padding: "2px 5px",
                            borderRadius: "3px",
                            fontSize: "10px",
                            fontWeight: "600",
                          }}
                        >
                          {bookingData.isPaid ? "Paid" : "Pending"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Amount Breakdown */}
                <div className="col-md-6">
                  <div
                    style={{
                      backgroundColor: "#2a2a2a",
                      padding: "12px",
                      borderRadius: "6px",
                      border: "1px solid #3d3d3d",
                      height: "100%",
                    }}
                  >
                    <div className="d-flex align-items-center mb-1">
                      <span style={{ fontSize: "14px", marginRight: "6px" }}>
                        💰
                      </span>
                      <h6 className="mb-0" style={{ color: "#28a745" }}>
                        Amount Details
                      </h6>
                    </div>
                    <div className="d-flex justify-content-between mb-1">
                      <small style={{ color: "#aaa" }}>Court Charges:</small>
                      <span style={{ fontWeight: "600" }}>
                        Rs.{" "}
                        {clubDiscount > 0
                          ? originalCourtCharges
                          : bookingData.courtcharges || 0}
                      </span>
                    </div>
                    {clubDiscount > 0 && (
                      <div className="d-flex justify-content-between mb-1">
                        <small style={{ color: "#aaa" }}>
                          Club Discount (
                          {clubDiscount % 1 === 0
                            ? clubDiscount
                            : clubDiscount.toFixed(2)}
                          %):
                        </small>
                        <span
                          style={{
                            fontWeight: "600",
                            color: "rgb(0, 243, 255)",
                          }}
                        >
                          -Rs.{" "}
                          {Math.round(
                            originalCourtCharges * (clubDiscount / 100)
                          )}
                        </span>
                      </div>
                    )}
                    {clubDiscount === 0 && bookingData.discountAmount > 0 && (
                      <div className="d-flex justify-content-between mb-1">
                        <small style={{ color: "#aaa" }}>Promo Discount:</small>
                        <span style={{ fontWeight: "600", color: "#28a745" }}>
                          -Rs. {bookingData.discountAmount || 0}
                        </span>
                      </div>
                    )}
                    <div className="d-flex justify-content-between mb-1">
                      <small style={{ color: "#aaa" }}>Platform Fee:</small>
                      <span style={{ fontWeight: "600" }}>
                        Rs. {bookingData.platformfee || 0}
                      </span>
                    </div>
                    <hr style={{ borderColor: "#3d3d3d", margin: "6px 0" }} />
                    <div className="d-flex justify-content-between mb-1">
                      <strong style={{ color: "#28a745" }}>
                        Total Amount:
                      </strong>
                      <strong style={{ color: "#28a745" }}>
                        Rs. {bookingData.total}
                      </strong>
                    </div>
                    <div className="d-flex justify-content-between mb-1">
                      <small style={{ color: "#aaa" }}>Paid Amount:</small>
                      <span style={{ fontWeight: "600" }}>
                        Rs. {bookingData.receivedAmount || 0}
                      </span>
                    </div>
                    {(bookingData.remainingAmount > 0 ||
                      bookingData.total - (bookingData.receivedAmount || 0) >
                      0) && (
                        <div className="d-flex justify-content-between">
                          <small style={{ color: "#aaa" }}>Remaining:</small>
                          <span style={{ fontWeight: "600", color: "#ffc107" }}>
                            Rs.{" "}
                            {bookingData.remainingAmount ||
                              bookingData.total -
                              (bookingData.receivedAmount || 0)}
                          </span>
                        </div>
                      )}
                  </div>
                </div>
              </div>

              {/* Promo Code Info (if applicable) */}
              {bookingData.promocode && (
                <div className="mt-2">
                  <div
                    style={{
                      backgroundColor: "#2a2a2a",
                      padding: "12px",
                      borderRadius: "8px",
                      border: "1px solid #28a745",
                    }}
                  >
                    <div className="d-flex align-items-center">
                      <span style={{ fontSize: "16px", marginRight: "8px" }}>
                        🎫
                      </span>
                      <div>
                        <small style={{ color: "#28a745" }}>
                          Promo Code Applied
                        </small>
                        <div style={{ fontWeight: "600" }}>
                          {bookingData.promocode}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div
              className="modal-footer justify-content-center"
              style={{
                borderTop: "1px solid #3d3d3d",
                padding: "12px",
              }}
            >
              <button
                type="button"
                className="btn btn-success px-3"
                onClick={() => {
                  onClose();
                  navigate("/dashboard?tab=bookings");
                }}
                style={{
                  backgroundColor: "#28a745",
                  borderColor: "#28a745",
                  fontWeight: "600",
                  borderRadius: "6px",
                  padding: "8px 20px",
                }}
              >
                <span style={{ marginRight: "5px" }}>📋</span>
                View My Bookings
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const TermsAndConditionsModal = ({ show, onClose, terms }) => {
    if (!show) return null;

    // If terms are empty or undefined, don't render the modal
    if (!terms || !terms.trim()) {
      return null;
    }

    return (
      <div
        className="modal show d-block"
        style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content bg-dark text-white">
            <div className="modal-header border-secondary">
              <h5 className="modal-title">Terms & Conditions</h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={onClose}
              ></button>
            </div>
            <div className="modal-body">
              <ul>
                {terms
                  .split(".")
                  .map((term, index) =>
                    term.trim() ? <li key={index}>{term.trim()}.</li> : null
                  )}
              </ul>
            </div>

            <div className="modal-footer border-secondary">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // API calls
  const getData = async () => {
    try {
      const [
        sportsResponse,
        offPeakResponse,
        clubAccountsResponse,
        termsResponse,
        clubDiscountResponse,
        bankDiscountsResponse,
      ] = await Promise.all([
        Common.ApiService.getInstance().request("GetSportWiseCourtddl"),
        Common.ApiService.getInstance().request("GetOffPeakCharges"),
        Common.ApiService.getInstance().request("GetClubAccounts"),
        Common.ApiService.getInstance().request("GetTermsnConditions"),
        Common.ApiService.getInstance().request("GetClubActiveDiscount"),
        Common.ApiService.getInstance().request("GetClubBankActiveDiscounts", null, "GET"),
      ]);

      // Handle sports response
      if (sportsResponse.status === respEnum.Success) {
        const sportsWithSelection = sportsResponse.data;
        dispatch(setSports(sportsWithSelection));
      } else {
        await Swal.fire({
          icon: "error",
          title: "Error",
          text: sportsResponse.message || "Failed to load sports data",
        });
      }

      // Handle off-peak response
      if (offPeakResponse.status === respEnum.Success) {
        dispatch(setOffPeakRates(offPeakResponse.data));
      } else {
        await Swal.fire({
          icon: "error",
          title: "Error",
          text: offPeakResponse.message || "Failed to load off-peak rates",
        });
      }

      // Handle club accounts response
      if (clubAccountsResponse.status === respEnum.Success) {
        const paymentOptions = InitPaymentOptions(clubAccountsResponse.data);
        const firstActiveOption = paymentOptions.find(
          (option) => option.isActive
        );
        if (firstActiveOption) {
          dispatch(setSelectedPaymentTypeId(firstActiveOption.paymentTypeId));
        }
        dispatch(setClubAccounts(paymentOptions));
      } else {
        await Swal.fire({
          icon: "error",
          title: "Error",
          text:
            clubAccountsResponse.message || "Failed to load payment options",
        });
      }

      // Handle terms response
      if (termsResponse.status === respEnum.Success) {
        dispatch(setTermsAndConditions(termsResponse.data));
      } else {
        await Swal.fire({
          icon: "error",
          title: "Error",
          text: termsResponse.message || "Failed to load terms and conditions",
        });
      }

      // Handle club discount response
      if (clubDiscountResponse.status === respEnum.Success) {
        const discountValue = clubDiscountResponse.data;
        // Set club discount (null or value greater than 0) - keep decimal values
        dispatch(
          setClubDiscount(
            discountValue && discountValue > 0
              ? Number(discountValue.toFixed(2))
              : 0
          )
        );
      } else {
        // Set default to 0 if API fails
        dispatch(setClubDiscount(0));
      }

      // Handle bank discounts response
      if (bankDiscountsResponse && bankDiscountsResponse.status === respEnum.Success) {
        // Filter discounts where isdisplayonapp is true
        const filteredBankDiscounts = bankDiscountsResponse.data.map((bank) => ({
          ...bank,
          discounts: bank.discounts.filter((discount) => discount.isdisplayonapp === true),
        })).filter((bank) => bank.discounts.length > 0); // Only include banks with at least one displayable discount
        dispatch(setBankDiscounts(filteredBankDiscounts));
      } else {
        // Set default to empty array if API fails
        dispatch(setBankDiscounts([]));
      }
    } catch (error) {
      // Reset any loaders that might be active
      setShowPadelLoader(false);

      await Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "An error occurred while loading data",
      });
    }
  };

  const getTimeSlots = async (
    courtId = selectedCourt,
    duration = selectedDuration,
    date = selectedDate,
    showLoader = true // Add parameter to control loader display
  ) => {
    try {
      // Only show loader if explicitly requested
      if (showLoader) {
        setLoaderMessage("Fetching available time slots...");
        setShowPadelLoader(true);
      }

      // Ensure we're working with a proper date format
      let formattedDate;
      if (typeof date === "string" && date.includes("T")) {
        formattedDate = date.split("T")[0];
      } else {
        // If it's a Date object or another format, convert it
        const dateObj = new Date(date);
        formattedDate = `${dateObj.getFullYear()}-${String(
          dateObj.getMonth() + 1
        ).padStart(2, "0")}-${String(dateObj.getDate()).padStart(2, "0")}`;
      }

      const url = `CheckCourtAvailabilityAdvance?playDate=${formattedDate}&courtId=${courtId}&duration=${duration}`;
      const response = await Common.ApiService.getInstance().request(url);
      setShowPadelLoader(false);
      if (response.status === respEnum.Success) {
        const transformedSlots = response.data
          .filter((slot) => isTimeBookable(slot.start))
          .map((slot) => ({
            ...slot,
            isClickable: slot.status === 1,
            displayText:
              slot.status === 1
                ? Utility.formatTimeToAMPM(slot.start)
                : `❌ ${Utility.formatTimeToAMPM(slot.start)}`,
          }));
        dispatch(setTimeSlots(transformedSlots));
      } else {
        if (showLoader) {
          await Swal.fire({
            icon: "error",
            title: "Error",
            text: response.message || "Failed to load time slots",
          });
        }
        dispatch(setTimeSlots([]));
      }
    } catch (error) {
      if (showLoader) {
        await Swal.fire({
          icon: "error",
          title: "Error",
          text: error.message || "Failed to load time slots",
        });
      }
      dispatch(setTimeSlots([]));
    } finally {
      // Only hide loader if it was shown
      if (showLoader) {
        setShowPadelLoader(false);
      }
    }
  };

  // Helper function to handle booking completion (success or error)
  const handleBookingCompletion = () => {
    console.log("🔄 Resetting all booking states and loaders");
    setShowPadelLoader(false);
    setIsBookingInProgress(false);
    setIsProcessingCheckout(false);
    setLoaderMessage("Loading..."); // Reset loader message
  };

  const handleProceedBooking = async () => {
    // Prevent double booking
    if (isBookingInProgress) {
      console.warn("Booking already in progress, ignoring duplicate request");
      return;
    }

    // Rate limiting check using security helper
    const userIdentifier = getUser()?.id || "anonymous";
    if (!checkRateLimit(`booking_${userIdentifier}`, 30000, 3)) {
      logSecurityEvent("rate_limit_exceeded", {
        action: "booking_attempt",
        user: userIdentifier,
      });
      await Swal.fire({
        icon: "warning",
        title: "Please Wait",
        text: "Please wait before making another booking attempt",
      });
      return;
    }

    // Input validation
    if (
      !selectedSport ||
      !selectedCourt ||
      !selectedTime ||
      !selectedDuration
    ) {
      await Swal.fire({
        icon: "warning",
        title: "Incomplete Selection",
        text: "Please select all required booking details",
      });
      return;
    }

    try {
      // Set booking in progress flag
      setIsBookingInProgress(true);

      // Check if user is logged in
      const user = getUser();

      if (!user) {
        // Set booking flow flag and show login modal
        dispatch(setIsBookingFlow(true));
        setShowLoginModal(true);
        return;
      }

      setIsProcessingCheckout(true);

      const applicableTerms = termsAndConditions.find((t) => {
        if (
          selectedPaymentTypeId === PaymentTypeEnum.Pay_On_Club ||
          selectedPaymentTypeId === "Pay_On_Club"
        ) {
          return t.type === 3;
        } else if (
          selectedPaymentTypeId === PaymentTypeEnum.Payment_GateWay ||
          selectedPaymentTypeId === "Payment_GateWay" ||
          selectedPaymentTypeId === PaymentTypeEnum.Online_Partial_Pay ||
          selectedPaymentTypeId === "Online_Partial_Pay"
        ) {
          return t.type === 2;
        }
        return false;
      });

      if (applicableTerms?.condition && applicableTerms.condition.trim()) {
        setShowBookingTermsModal(true);
        return;
      }

      await processBooking();
    } catch (error) {
      console.error("❌ Booking process error:", error);

      // Show error message to user
      await Swal.fire({
        icon: "error",
        title: "Booking Error",
        text:
          error.message ||
          "An unexpected error occurred during booking. Please try again.",
        confirmButtonText: "OK",
      });
    } finally {
      // Always hide loader and reset states
      handleBookingCompletion();
    }
  };

  // Handle login success when in booking flow
  const handleLoginSuccess = () => {
    setShowLoginModal(false);
    setIsUserLoggedIn(true); // Update login status
    dispatch(setIsBookingFlow(false));
    // Don't automatically proceed with booking after login to prevent double booking
    // User needs to click "Book Now" again
  };

  // Handle login modal close and check if user logged in during booking flow
  const handleLoginModalClose = () => {
    const user = getUser();
    if (user && isBookingFlow) {
      handleLoginSuccess();
    } else {
      setShowLoginModal(false);
      dispatch(setIsBookingFlow(false));
    }
    // Reset booking state
    handleBookingCompletion();
  };

  const handlePayOnClubBooking = async (payOnClubPayload) => {
    try {
      // Show loader for booking process
      setLoaderMessage("Processing your booking...");
      setShowPadelLoader(true);

      const response = await Common.ApiService.getInstance().request(
        "AddBooking",
        payOnClubPayload,
        "POST",
        true,
        true,
        true
      );

      setShowPadelLoader(false);
      // Check if response exists before accessing status
      if (response && response.status === respEnum.Success) {
        // Prepare booking data for the success modal
        const selectedSportObj = sports.find((s) => s.label === selectedSport);
        const selectedCourtObj = selectedSportObj?.options.find(
          (c) => c.value === selectedCourt
        );

        const bookingData = {
          ...payOnClubPayload,
          ...response.data, // Merge with API response data
          refNo: response.data,
          courtName: selectedCourtObj?.label,
          sportName: selectedSport,
        };

        setBookingSuccessData(bookingData);
        setShowBookingSuccessModal(true);

        // Refresh time slots after successful booking to avoid overlap issues
        if (selectedCourt && selectedDuration) {
          setTimeout(() => {
            getTimeSlots(selectedCourt, selectedDuration, selectedDate, false); // Don't show loader
          }, 500);
        }
      } else {
        // Handle failed response status
        await Swal.fire({
          icon: "error",
          title: "Booking Failed",
          text:
            response?.message ||
            "Failed to process your booking. Please try again.",
          confirmButtonText: "OK",
        });

        // Refresh time slots if the error might be related to slot availability
        if (
          response?.message &&
          response.message.toLowerCase().includes("slot")
        ) {
          if (selectedCourt && selectedDuration) {
            setTimeout(() => {
              getTimeSlots(
                selectedCourt,
                selectedDuration,
                selectedDate,
                false
              ); // Don't show loader
            }, 500);
          }
        }
      }
      return response;
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "Booking Failed",
        text: error.message || "Failed to process booking",
        confirmButtonText: "OK",
      });

      // Refresh time slots after any booking error to ensure fresh data
      if (selectedCourt && selectedDuration) {
        setTimeout(() => {
          getTimeSlots(selectedCourt, selectedDuration, selectedDate, false); // Don't show loader
        }, 500);
      }

      // Return an error response object instead of throwing
      return {
        status: respEnum.Error,
        message: error.message || "Failed to process booking",
      };
    } finally {
      // Always hide all loaders and reset states
      handleBookingCompletion();
    }
  };

  // Add new function to handle booking process
  const processBooking = async () => {
    try {
      const transactionToken =
        Date.now().toString() + Math.random().toString(36).substring(2);

      const selectedSportObj = sports.find((s) => s.label === selectedSport);
      const selectedCourtObj = selectedSportObj?.options.find(
        (c) => c.value === selectedCourt
      );
      const selectedAccount = clubAccounts.find(
        (account) => account.paymentTypeId === selectedPaymentTypeId
      );

      if (!selectedCourtObj || !selectedAccount) {
        await Swal.fire({
          icon: "error",
          title: "Booking Failed",
          text: "Required booking information is missing",
          confirmButtonText: "OK",
        });
        handleBookingCompletion(); // Reset all loader states
        return; // Stop execution instead of throwing
      }

      // Create correct datetime with timezone consideration
      const [startHours, startMinutes] = selectedTime.split(":");

      // Format date parts
      let dateObj;
      if (typeof selectedDate === "string" && selectedDate.includes("T")) {
        dateObj = new Date(selectedDate);
      } else {
        dateObj = new Date(selectedDate);
      }

      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, "0");
      const day = String(dateObj.getDate()).padStart(2, "0");
      const formattedDate = `${year}-${month}-${day}`;

      // Create time strings with correct hours and minutes
      const startTimeStr = `${startHours}:${startMinutes}:00`;
      const endHour = Math.floor(
        (parseInt(startHours) * 60 +
          parseInt(startMinutes) +
          selectedDuration) /
        60
      );
      const endMinute =
        (parseInt(startHours) * 60 +
          parseInt(startMinutes) +
          selectedDuration) %
        60;

      // Handle 24:00:00 case by converting to 23:59:00
      let finalEndHour = endHour;
      let finalEndMinute = endMinute;

      if (endHour === 24 && endMinute === 0) {
        finalEndHour = 23;
        finalEndMinute = 59;
      }

      const endTimeStr = `${String(finalEndHour).padStart(2, "0")}:${String(
        finalEndMinute
      ).padStart(2, "0")}:00`;

      // Format as yyyy-MM-ddTHH:mm:ss format without timezone information
      const startDateTimeStr = `${formattedDate}T${startTimeStr}`;
      const endDateTimeStr = `${formattedDate}T${endTimeStr}`;

      // Handle Pay on Club separately
      if (
        selectedPaymentTypeId === PaymentTypeEnum.Pay_On_Club ||
        selectedPaymentTypeId === "Pay_On_Club"
      ) {
        // Create Pay on Club payload
        const payOnClubPayload = {
          courtId: selectedCourt,
          bookingMode: BookingModeEnum.Web_Booking,
          courtName: selectedCourtObj?.label,
          paymentTypeId: PaymentTypeEnum.Pay_On_Club, // Use the enum value instead of selectedPaymentTypeId
          publicBookingPrivacyType: null,
          bookingPlayerPaymentStatus: PaymentStatusEnum.Un_Paid,
          isPaid: false,
          clubAccountId: selectedAccount?.id,
          matchType: MatchTypeEnum.Friendly,
          paymentMethod: BookingPaymentMethodEnum.Creator_Pays_All,
          bookingType: BookingTypeEnum.Private,
          noofplayers: 4,
          courtcharges: clubDiscount > 0 ? originalCourtCharges : courtCharges, // Send original amount if club discount exists
          originalCourtCharges: originalCourtCharges,
          clubDiscount: clubDiscount,
          platformfee: 0,
          total: subTotal,
          receivedAmount: 0,
          isNoplatformFee: true,
          bookingDate: `${formattedDate}T${new Date().toISOString().split("T")[1]
            }`,
          startDateTime: startDateTimeStr,
          endDateTime: endDateTimeStr,
          paymentReceipt: "",
          invites: [],
        };

        // Add promo code details if applicable, or club discount details
        if (promoCodeDetails && clubDiscount === 0) {
          Object.assign(payOnClubPayload, {
            promocode: promoCode,
            promocodeId: promoCodeDetails.id,
            promocodeAmt: discount,
            orderAmount:
              originalCourtCharges > 0 ? originalCourtCharges : courtCharges,
            discountAmount: discount,
            promocodeTransactionLogType:
              TransactionLogTypeEnum.PromoCode_Redeemed,
            redeemPercent: promoCodeDetails.discountValue,
            promocodePercent: promoCodeDetails.discountValue,
          });
        } else if (clubDiscount > 0) {
          // Add club discount details
          Object.assign(payOnClubPayload, {
            promocodeAmt: originalCourtCharges - courtCharges,
            orderAmount: originalCourtCharges,
            discountAmount: originalCourtCharges - courtCharges,
            promocodePercent: clubDiscount,
            redeemPercent: clubDiscount,
            isCapped: false,
            promocodeTransactionLogType: TransactionLogTypeEnum.Club_Discount,
          });
        }

        const payOnClubResponse = await handlePayOnClubBooking(
          payOnClubPayload
        );

        // Check if response exists before accessing status
        if (
          payOnClubResponse &&
          payOnClubResponse.status === respEnum.Success
        ) {
          // Success is already handled in handlePayOnClubBooking function
        } else {
          // Error is already handled in handlePayOnClubBooking function
        }
        return; // Stop execution instead of throwing
      }

      // Calculate payment amount based on payment type
      let paymentAmount;
      if (
        selectedPaymentTypeId === PaymentTypeEnum.Payment_GateWay ||
        selectedPaymentTypeId === "Payment_GateWay"
      ) {
        // Full payment - use complete subtotal
        paymentAmount = subTotal;
      } else if (
        selectedPaymentTypeId === PaymentTypeEnum.Online_Partial_Pay ||
        selectedPaymentTypeId === "Online_Partial_Pay"
      ) {
        // Partial payment - use advance payment amount
        paymentAmount = advancePayment;
      } else {
        // Pay on club - no online payment required
        paymentAmount = 0;
      }

      // Use testing amount if testing flag is enabled
      if (isTestingPayment) {
        console.log("🧪 Testing mode enabled - PayFast amount set to Rs. 2");
        paymentAmount = 2;
      }

      // Set bookingPlayerPaymentStatus
      let bookingPlayerPaymentStatus;
      if (
        selectedPaymentTypeId === PaymentTypeEnum.Payment_GateWay ||
        selectedPaymentTypeId === "Payment_GateWay"
      ) {
        bookingPlayerPaymentStatus = PaymentStatusEnum.Paid;
      } else if (
        selectedPaymentTypeId === PaymentTypeEnum.Online_Partial_Pay ||
        selectedPaymentTypeId === "Online_Partial_Pay"
      ) {
        bookingPlayerPaymentStatus = PaymentStatusEnum.Partial_Paid;
      } else if (
        selectedPaymentTypeId === PaymentTypeEnum.Pay_On_Club ||
        selectedPaymentTypeId === "Pay_On_Club"
      ) {
        bookingPlayerPaymentStatus = PaymentStatusEnum.Un_Paid;
      }

      // Show loader for PayFast processing
      setLoaderMessage("Generating payment link...");
      setShowPadelLoader(true);

      // Create booking payload
      const bookingPayload = {
        courtId: Number(selectedCourt),
        courtName: selectedCourtObj?.label,
        bookingMode: BookingModeEnum.Web_Booking,
        paymentTypeId: Number(selectedPaymentTypeId),
        publicBookingPrivacyType: null,
        bookingPlayerPaymentStatus,
        isPaid: bookingPlayerPaymentStatus === PaymentStatusEnum.Paid,
        clubAccountId: selectedAccount?.id,
        matchType: MatchTypeEnum.Friendly,
        paymentMethod: BookingPaymentMethodEnum.Creator_Pays_All,
        bookingType: BookingTypeEnum.Private,
        noofplayers: 4,
        courtcharges: clubDiscount > 0 ? originalCourtCharges : courtCharges, // Send original amount if club discount exists
        originalCourtCharges: originalCourtCharges,
        clubDiscount: clubDiscount,
        platformfee: 0,
        total: subTotal,
        totalAmount: subTotal,
        finalAmount: subTotal,
        receivedAmount: paymentAmount,
        isNoplatformFee: true,
        bookingDate: `${formattedDate}T${new Date().toISOString().split("T")[1]
          }`,
        startDateTime: startDateTimeStr,
        endDateTime: endDateTimeStr,
        paymentReceipt: "",
        invites: [],
        transactionToken,
      };

      let finalBookingPayload = bookingPayload;

      if (promoCodeDetails && clubDiscount === 0) {
        // Apply promo code details
        finalBookingPayload = {
          ...bookingPayload,
          promocode: sanitizeInput(promoCode, "promo_code"),
          promocodeId: promoCodeDetails.id,
          promocodeAmt: discount,
          orderAmount:
            originalCourtCharges > 0 ? originalCourtCharges : courtCharges,
          discountAmount: discount,
          promocodeTransactionLogType:
            TransactionLogTypeEnum.PromoCode_Redeemed,
          redeemPercent: promoCodeDetails.discountValue,
          promocodePercent: promoCodeDetails.discountValue,
        };
      } else if (clubDiscount > 0) {
        // Apply club discount details
        finalBookingPayload = {
          ...bookingPayload,
          promocodeAmt: originalCourtCharges - courtCharges,
          orderAmount: originalCourtCharges,
          discountAmount: originalCourtCharges - courtCharges,
          promocodePercent: clubDiscount,
          redeemPercent: clubDiscount,
          isCapped: false,
          promocodeTransactionLogType: TransactionLogTypeEnum.Club_Discount,
        };
      }

      // Validate the booking payload before proceeding
      const validatedPayload = validateBookingPayload(finalBookingPayload);
      if (!validatedPayload) {
        logSecurityEvent("invalid_booking_payload", {
          payload: finalBookingPayload,
        });
        await Swal.fire({
          icon: "error",
          title: "Booking Failed",
          text: "Invalid booking data. Please try again.",
          confirmButtonText: "OK",
        });
        handleBookingCompletion(); // Reset all loader states
        return;
      }

      // Store booking payload (use validated payload)
      dispatch(setBookingPayload(validatedPayload));
      // Store encrypted booking payload with expiry (use validated payload)
      const encryptedPayload = btoa(
        JSON.stringify({
          data: validatedPayload,
          expiry: Date.now() + 15 * 60 * 1000, // Reduced to 15 minutes for security
          transactionToken,
        })
      );
      localStorage.setItem("bookingPayload", encryptedPayload);

      // Create PayFast request
      const payFastRequest = {
        transactionType: TransactionLogTypeEnum.Court_Booking,
        amount: paymentAmount, // This will be 2 if testing is enabled
        courtId: selectedCourt,
        originalCourtCharges: originalCourtCharges,
        clubDiscount: clubDiscount,
        startDatetime: startDateTimeStr,
        endDatetime: endDateTimeStr,
        bookingDate: `${formattedDate}T${new Date().toISOString().split("T")[1]
          }`,
        clubAccountId: selectedAccount?.id,
        paymentTypeId: selectedPaymentTypeId,
        requestPayload: JSON.stringify(validatedPayload),
      };

      let finalPayFastRequest = payFastRequest;

      if (promoCodeDetails && clubDiscount === 0) {
        // Apply promo code details
        finalPayFastRequest = {
          ...payFastRequest,
          promocodeId: promoCodeDetails.id,
          promocode: promoCode,
          promocodeAmt: discount,
          orderAmount:
            originalCourtCharges > 0 ? originalCourtCharges : courtCharges,
          promocodePercent: promoCodeDetails.discountValue,
          redeemPercent: promoCodeDetails.discountValue,
          isCapped: !!promoCodeDetails.maxDiscount,
          promocodeTransactionLogType:
            TransactionLogTypeEnum.PromoCode_Redeemed,
        };
      } else if (clubDiscount > 0) {
        // Apply club discount details
        finalPayFastRequest = {
          ...payFastRequest,
          promocodeAmt: originalCourtCharges - courtCharges,
          orderAmount: originalCourtCharges,
          promocodePercent: clubDiscount,
          redeemPercent: clubDiscount,
          isCapped: false,
          promocodeTransactionLogType: TransactionLogTypeEnum.Club_Discount,
        };
      }

      // Generate PayFast token
      const response = await Common.ApiService.getInstance().request(
        "GeneratePayFastToken",
        finalPayFastRequest,
        "POST",
        true,
        true,
        true
      );

      setShowPadelLoader(false);

      // Check if response exists before accessing status
      if (!response) {
        await Swal.fire({
          icon: "error",
          title: "Booking Failed",
          text: "No response received from payment service",
          confirmButtonText: "OK",
        });
        handleBookingCompletion(); // Reset all loader states
        return;
      }

      if (response.status === respEnum.Success) {
        const payFastData = {
          ...response.data[0],
          succesS_URL: `${window.location.origin}/payment/status`,
          failurE_URL: `${window.location.origin}/payment/status`,
        };

        openUrl(
          "https://ipg1.apps.net.pk/Ecommerce/api/Transaction/PostTransaction",
          payFastData
        );
      } else {
        // Show error message without throwing
        await Swal.fire({
          icon: "error",
          title: "Booking Failed",
          text: response.message || "Failed to generate payment token",
          confirmButtonText: "OK",
        });

        // Reset loader states
        handleBookingCompletion();

        // Refresh time slots after any PayFast booking failure
        if (selectedCourt && selectedDuration) {
          setTimeout(() => {
            getTimeSlots(selectedCourt, selectedDuration, selectedDate, false); // Don't show loader
          }, 500);
        }
      }
    } catch (error) {
      // Show error without rethrowing
      await Swal.fire({
        icon: "error",
        title: "Booking Failed",
        text: error.message || "An error occurred during booking",
        confirmButtonText: "OK",
      });

      // Refresh time slots after any PayFast booking error
      if (selectedCourt && selectedDuration) {
        setTimeout(() => {
          getTimeSlots(selectedCourt, selectedDuration, selectedDate, false); // Don't show loader
        }, 500);
      }
    } finally {
      // Always hide all loaders and reset states
      handleBookingCompletion();
    }
  };

  // Add error handling to openUrl function
  const openUrl = (url, body) => {
    try {
      const form = document.createElement("form");
      form.method = "POST";
      form.action = url;
      form.target = "_self";
      form.style.display = "none";

      Object.entries(body).forEach(([key, value]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = key;
        input.value = value;
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    } catch (error) {
      // Reset loader states when redirect fails
      handleBookingCompletion();
      Swal.fire({
        icon: "error",
        title: "Payment Failed",
        text: "Failed to redirect to payment gateway. Please try again.",
      });
    }
  };

  const BookingTermsModal = ({ show, onClose }) => {
    if (!show) return null;

    const applicableTerms = termsAndConditions.find((t) => {
      if (
        selectedPaymentTypeId === PaymentTypeEnum.Pay_On_Club ||
        selectedPaymentTypeId === "Pay_On_Club"
      ) {
        return t.type === 3;
      } else if (
        selectedPaymentTypeId === PaymentTypeEnum.Payment_GateWay ||
        selectedPaymentTypeId === "Payment_GateWay" ||
        selectedPaymentTypeId === PaymentTypeEnum.Online_Partial_Pay ||
        selectedPaymentTypeId === "Online_Partial_Pay"
      ) {
        return t.type === 2;
      }
      return false;
    });

    // If no terms are available or they're empty, proceed with booking directly
    if (!applicableTerms?.condition || !applicableTerms.condition.trim()) {
      // Prevent double booking
      if (isBookingInProgress) {
        console.warn("Booking already in progress, ignoring terms bypass");
        return null;
      }

      // Close modal and proceed with booking immediately
      onClose();
      processBooking();
      return null;
    }

    const handleAccept = async () => {
      // Prevent double booking
      if (isBookingInProgress) {
        console.warn("Booking already in progress, ignoring terms accept");
        return;
      }

      setShowBookingTermsModal(false);
      await processBooking();
    };

    return (
      <div
        className="modal show d-block"
        style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content bg-dark text-white">
            <div className="modal-header border-secondary">
              <h5 className="modal-title">Booking Terms & Conditions</h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={onClose}
              ></button>
            </div>
            <div className="modal-body">
              <ul>
                {applicableTerms.condition
                  .split(".")
                  .map((term, index) =>
                    term.trim() ? <li key={index}>{term.trim()}.</li> : null
                  )}
              </ul>
            </div>
            <div className="modal-footer border-secondary">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
              >
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleAccept}
                disabled={isBookingInProgress}
              >
                {isBookingInProgress ? "Processing..." : "Accept & Continue"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <style>{`
        .modal-content {
          margin-top: 200px !important;
          margin-bottom: 50px !important;
        }
          @media screen and (max-width: 991px) {
    body, html {
        font-size: 15px;
    }
}

        /* Card Offers Styles */
        .card-offers-title {
          color: #00f3ff !important;
        }

        .bank-card {
          cursor: pointer;
          backdrop-filter: blur(10px);
          border-radius: 12px;
          padding: 5px;
          transition: all 0.3s ease;
          height: 100%;
          position: relative;
          overflow: hidden;
              background: white;
          border: 1px solid rgba(255, 255, 255, 0.1);
          box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
        }

        .bank-card:hover {
                  border: 2px solid #2bbdca;
    border-color: #21ccd7;
        }

        .bank-card.selected {
              background: rgb(44 44 44);
          border: 2px solid rgba(0, 243, 255, 0.5);
          box-shadow: 0 8px 20px rgba(0, 243, 255, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1);
        }

       .bank-card.selected:hover {
    background: rgb(33 37 41);
          border-color: rgba(0, 243, 255, 0.5);
        }

        .bank-card-gradient-top {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 2px;
          background: linear-gradient(90deg, transparent, rgba(0, 243, 255, 0.8), transparent);
        }

        .bank-icon-container {
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .bank-logo-img {
          max-width: 50px;
          max-height: 40px;
          object-fit: contain;
        }

        .bank-name {
        font-size: 10px;
          color:black;
        }

        .bank-name.selected {
          color: #00f3ff;
        }

        .card-offer-item {
          cursor: pointer;
          backdrop-filter: blur(10px);
          border-radius: 8px;
          padding: 10px 12px;
          margin-bottom: 0;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
          flex-shrink: 0;
        }

        .card-offer-item:hover {
          background: rgba(255, 255, 255, 0.08);
          border-color: rgba(0, 243, 255, 0.3);
        }

        .card-offer-item.selected {
          background: rgba(47, 65, 87, 0.25);
          border: 2px solid rgba(0, 243, 255, 0.5);
          box-shadow: 0 8px 20px rgba(0, 243, 255, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1);
        }

        .card-offer-item.selected:hover {
          background: rgba(47, 65, 87, 0.25);
          border-color: rgba(0, 243, 255, 0.5);
        }

        .card-offer-name {
          color: #fff;
          font-weight: 400;
          font-size: 13px;
          margin-bottom: 4px;
        }

        .card-offer-name.selected {
          color: #00f3ff;
          font-weight: 500;
        }

        .card-offer-details {
          font-size: 10px;
          color: rgba(255, 255, 255, 0.7);
          line-height: 1.3;
        }

        .card-offer-discount-btn {
          background: linear-gradient(135deg, #00f2fecc 0%, #577c8e 100%);
          color: #fff;
          border: 1px solid rgba(0, 243, 255, 0.3);
          min-width: 60px;
          padding: 4px 10px;
          border-radius: 6px;
          font-weight: 600;
          font-size: 11px;
          box-shadow: 0 4px 12px rgba(47, 65, 87, 0.4);
          transition: all 0.3s ease;
        }

        .card-offer-discount-btn:hover {
          background: linear-gradient(135deg, #3d5470 0%, #2a3d52 100%);
          border-color: rgba(0, 243, 255, 0.5);
          box-shadow: 0 6px 16px rgba(0, 243, 255, 0.3);
        }

        /* Scrollable Bank Cards Container */
        .bank-cards-scroll-container {
          overflow-x: auto;
          overflow-y: hidden;
          padding-bottom: 8px;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: thin; /* Firefox */
          scrollbar-color: rgba(0, 243, 255, 0.6) rgba(255, 255, 255, 0.05);
        }
        
        /* Custom Thin Scrollbar Styling - Horizontal for Bank Cards */
        .bank-cards-scroll-container::-webkit-scrollbar {
          height: 4px;
        }

        .bank-cards-scroll-container::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 10px;
        }

        .bank-cards-scroll-container::-webkit-scrollbar-thumb {
          background: rgba(0, 243, 255, 0.6);
          border-radius: 10px;
          transition: background 0.3s ease;
        }

        .bank-cards-scroll-container::-webkit-scrollbar-thumb:hover {
          background: rgba(0, 243, 255, 0.9);
        }

        .bank-cards-wrapper {
          display: flex;
          gap: 8px;
          min-width: min-content;
        }

        .bank-card-col {
          flex: 0 0 auto;
          min-width: calc(33.333% - 6px);
          width: calc(33.333% - 6px);
        }

        @media (max-width: 768px) {
        
            min-width: calc(50% - 4px);
            width: calc(50% - 4px);
          }
            .card-offer-name {
    color: #fff;
    font-weight: 400;
    font-size: 11px;}
        }

        @media (max-width: 576px) {
          .bank-card-col {
            min-width: calc(45% - 4px);
            width: calc(45% - 4px);
          }
        }

        /* Scrollable Card Offers Container */
        .card-offers-scroll-container {
          overflow-x: hidden;
          overflow-y: auto;
          padding-right: 8px;
          max-height: 400px;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none; /* Firefox */
          -ms-overflow-style: none; /* IE and Edge */
        }
        
        .card-offers-scroll-container::-webkit-scrollbar {
          display: none; /* Chrome, Safari, Opera */
        }

        .card-offers-wrapper {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
      `}</style>
      <Spacing lg="70" md="70" />
      <Spacing lg="140" md="80" />
      <SectionHeadingStyle3
        title="Book your court"
        subTitle="Check Availability"
        variant="text-center"
      />
      <Spacing lg="75" md="60" />

      <div className="container-fluid bg-dark min-vh-100 py-5">
        <div className="container">
          {/* Date Selection Section */}
          <div className="row">
            <div className="col-md-6 mb-4">
              <div className="card bg-dark text-white border-secondary">
                <div className="card-body">
                  <h2 className="h4 mb-4">Step 1: Select Date</h2>
                  <div className="bg-secondary rounded p-4">
                    <div className="d-flex justify-content-between align-items-center">
                      <button
                        className="btn btn-outline-light"
                        onClick={() => handleDateChange(-1)}
                        disabled={
                          new Date(selectedDate).setHours(0, 0, 0, 0) <=
                          new Date().setHours(0, 0, 0, 0)
                        }
                      >
                        <ChevronLeft />
                      </button>
                      <div className="text-center position-relative">
                        <h3 className="h5 mb-0">
                          {Utility.formatDate(selectedDate)}{" "}
                          <button
                            type="button"
                            className="btn btn-link text-white p-0 "
                            onClick={() =>
                              document
                                .getElementById("hiddenDatePicker")
                                .showPicker()
                            }
                            title="Select date from calendar"
                            style={{ fontSize: "18px", textDecoration: "none" }}
                          >
                            📅
                          </button>
                        </h3>
                        {/* Calendar Icon */}

                        {/* Hidden Date Input */}
                        <input
                          id="hiddenDatePicker"
                          type="date"
                          style={{
                            position: "absolute",
                            left: 0,
                            top: 0,
                            width: "100%",
                            height: "100%",
                            opacity: 0,
                          }}
                          value={getCurrentDateValue()}
                          min={getMinDate()}
                          max={getMaxDate()}
                          onChange={handleCalendarDateChange}
                        />
                      </div>
                      <button
                        className="btn btn-outline-light"
                        onClick={() => handleDateChange(1)}
                        disabled={
                          new Date(selectedDate).setHours(0, 0, 0, 0) >=
                          new Date(getMaxDate()).setHours(0, 0, 0, 0)
                        }
                      >
                        <ChevronRight />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sport Selection Section */}
            <div className="col-md-6 mb-4">
              <div className="card bg-dark text-white border-secondary">
                <div className="card-body">
                  <h2 className="h4 mb-4">Step 2: Select Sport</h2>
                  <div className="row g-3 mb-1">
                    {sports.map((sport) => (
                      <div className="col-6 col-md-4" key={sport.label}>
                        <button
                          onClick={() => handleSportSelect(sport.label)}
                          className={`btn btn-hover-cus ${sport.isSelected ? "btn-light" : "btn-outline-light"
                            } w-100 p-2 pt-3 pb-3`}
                          disabled={isProcessingCheckout}
                        >
                          <h3 className="h5 mb-2">{sport.label}</h3>
                          <small className="d-block text-white">
                            {sport.options.length}{" "}
                            {sport.options.length === 1 ? "Court" : "Courts"}{" "}
                            Available
                          </small>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Court Selection Section */}
          {selectedSport && (
            <div className="card bg-dark text-white mb-4 border-secondary">
              <div className="card-body">
                <h2 className="h4 mb-4">Step 3: Select Court</h2>
                <div className="row g-3 mb-1">
                  {sports
                    .find((s) => s.label === selectedSport)
                    ?.options.map((court) => (
                      <div
                        className="col-6 col-md-4 col-lg-3"
                        key={court.value}
                      >
                        <button
                          onClick={() => handleCourtSelect(court.value)}
                          className={`btn btn-hover-cus ${court.isSelected ? "btn-light" : "btn-outline-light"
                            } w-100 p-4 h-100`}
                          disabled={isProcessingCheckout}
                        >
                          <h4 className="h6 mb-2">
                            {/* {selectedSport}  */}
                            {court.label}
                          </h4>
                        </button>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}

          <div className="row">
            {/* Time and Duration Selection */}
            <div className="col-lg-8">
              {selectedCourt && (
                <div className="card bg-dark text-white border-secondary">
                  <div className="card-body">
                    <h2 className="h4 ">Step 4: Select Time & Duration</h2>
                    <div className="d-flex justify-content-end mb-4">
                      <div className="d-flex align-items-center gap-3">
                        <Clock className="text-light" />
                        <select
                          value={selectedDuration || ""}
                          onChange={(e) => handleDurationChange(e.target.value)}
                          className="form-select w-auto"
                          disabled={isProcessingCheckout}
                        >
                          {getSelectedCourtFormats().map((format) => (
                            <option key={format.id} value={format.value}>
                              {format.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="row g-3">
                      {availableTimeSlots?.map((slot) => (
                        <div
                          className="col-6 col-md-4 col-lg-2"
                          key={slot.start}
                        >
                          <button
                            onClick={() =>
                              slot.isClickable && handleTimeSelect(slot.start)
                            }
                            className={`btn ${selectedTime === slot.start
                              ? "btn-light"
                              : "btn-outline-light"
                              } w-100 ${!slot.isClickable ? "disabled opacity-50" : ""
                              }`}
                            disabled={isProcessingCheckout || !slot.isClickable}
                          >
                            {slot.displayText}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Booking Summary Section */}
            <div className="col-lg-4">
              {selectedCourt && selectedTime && (
                <div className="card bg-dark text-white border-secondary">
                  <div className="card-body">
                    <h2 className="h4 mb-3">Booking Summary</h2>
                    <div className="bg-secondary rounded p-3">
                      {/* Booking Details */}
                      <div className="d-flex justify-content-between mb-2">
                        <span>Date</span>
                        <span>{Utility.formatDate(selectedDate)}</span>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span>Sport</span>
                        <span>{selectedSport}</span>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span>Court</span>
                        <span>
                          {
                            sports
                              .find((s) => s.label === selectedSport)
                              ?.options.find((c) => c.value === selectedCourt)
                              ?.label
                          }
                        </span>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span>Time</span>
                        <span>
                          {calculateEndTime(selectedTime, selectedDuration)}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between mb-3">
                        <span>Duration</span>
                        <span>
                          {
                            getSelectedCourtFormats().find(
                              (f) => f.value === selectedDuration
                            )?.label
                          }
                        </span>
                      </div>

                      {/* Club Discount Notice */}
                      {clubDiscount > 0 && (
                        <div className="mb-3">
                          <div
                            className="alert py-3 px-3 mb-0"
                            style={{
                              backgroundColor: "rgba(0, 243, 255, 0.2)",
                              border: "2px solid rgb(0, 243, 255)",
                              borderRadius: "8px",
                              boxShadow: "0 4px 12px rgba(0, 243, 255, 0.3)",
                            }}
                          >
                            <div className="d-flex align-items-center">
                              <span
                                style={{
                                  fontSize: "24px",
                                  marginRight: "12px",
                                }}
                              >
                                🎉
                              </span>
                              <div>
                                <div
                                  className="mb-1"
                                  style={{
                                    fontWeight: "700",
                                    color: "rgb(0, 243, 255)",
                                    fontSize: "16px",
                                  }}
                                >
                                  Club Discount Active:{" "}
                                  {clubDiscount % 1 === 0
                                    ? clubDiscount
                                    : clubDiscount.toFixed(2)}
                                  % OFF
                                </div>
                                <div
                                  style={{
                                    fontSize: "14px",
                                    color: "rgb(0, 243, 255)",
                                    fontWeight: "600",
                                  }}
                                >
                                  You're saving Rs.{" "}
                                  {(
                                    originalCourtCharges - courtCharges
                                  ).toLocaleString()}{" "}
                                  on this booking!
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Promo Code Section - Only show if user is logged in and no club discount */}
                      {isUserLoggedIn && clubDiscount === 0 && (
                        <div className="mb-3">
                          <div className="input-group input-group-sm">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Promo Code"
                              value={promoCode}
                              onChange={(e) =>
                                dispatch(setPromoCode(e.target.value))
                              }
                              disabled={
                                promoCodeDetails !== null || clubDiscount > 0
                              }
                            />
                            {promoCodeDetails ? (
                              <button
                                className="btn btn-outline-danger px-3"
                                type="button"
                                onClick={handleRemovePromoCode}
                                title="Remove Promo Code"
                              >
                                <X size={16} />
                              </button>
                            ) : (
                              <button
                                className="btn btn-primary px-3"
                                type="button"
                                onClick={handlePromoCodeValidation}
                                disabled={clubDiscount > 0}
                              >
                                Apply
                              </button>
                            )}
                          </div>
                          {/* <div style={{
                            color: '#ffffff',
                            fontSize: '14px',
                            marginTop: '7px',
                            borderRadius: '5px',
                            padding: '4px',
                            border: '1px solid',
                            
                          }}>
                            <p style={{
                              marginBottom: '0px'
                            }}>Use code IGNITE2000 for Rs. 2,000 off.</p>
                          </div> */}
                          {promoCodeError && (
                            <div className="text-danger small mt-1">
                              {promoCodeError}
                            </div>
                          )}
                          {promoCodeDetails && (
                            <div className="text-info small mt-1">
                              <span
                                className="cursor-pointer"
                                onClick={() => setShowTermsModal(true)}
                                style={{ cursor: "pointer" }}
                              >
                                <i className="bi bi-info-circle me-1"></i>
                                View Terms & Conditions
                              </span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Price Summary */}
                      <div className="border-top border-white pt-2">
                        <div className="d-flex justify-content-between mb-2">
                          <span className="h6 mb-0">Court Charges</span>
                          <span className="h6 mb-0">
                            Rs.{" "}
                            {clubDiscount > 0
                              ? originalCourtCharges.toLocaleString()
                              : courtCharges.toLocaleString()}
                          </span>
                        </div>

                        {clubDiscount > 0 && (
                          <div className="d-flex justify-content-between mb-2">
                            <span style={{ color: "rgb(0, 243, 255)" }}>
                              Club Discount (
                              {clubDiscount % 1 === 0
                                ? clubDiscount
                                : clubDiscount.toFixed(2)}
                              %)
                            </span>
                            <span style={{ color: "rgb(0, 243, 255)" }}>
                              - Rs.{" "}
                              {(
                                originalCourtCharges - courtCharges
                              ).toLocaleString()}
                            </span>
                          </div>
                        )}

                        {discount > 0 && (
                          <div className="d-flex justify-content-between mb-2">
                            <span className="text-success">Promo Discount</span>
                            <span className="text-success">
                              - Rs. {discount.toLocaleString()}
                            </span>
                          </div>
                        )}

                        <div className="d-flex justify-content-between border-top border-white pt-2 mb-2">
                          <span className="h6 mb-0">Sub Total</span>
                          <span className="h6 mb-0">
                            Rs. {subTotal.toLocaleString()}
                          </span>
                        </div>

                        {/* Partial Payment Details */}
                        {selectedPaymentTypeId ===
                          PaymentTypeEnum.Online_Partial_Pay && (
                            <div className="border-top border-white pt-2">
                              <div className="d-flex justify-content-between mb-2">
                                <span className="text-info">Advance Payment</span>
                                <span className="text-info">
                                  Rs. {advancePayment.toLocaleString()}
                                </span>
                              </div>
                              <div className="d-flex justify-content-between">
                                <span className="text-warning">Pay at Club</span>
                                <span className="text-warning">
                                  Rs. {payOnClub.toLocaleString()}
                                </span>
                              </div>
                            </div>
                          )}
                      </div>

                      {/* Card Offers */}
                      <div className="mt-3">
                        <span className="h6 mb-3 d-block ">Card Offers</span>

                        {/* Bank Selection */}
                        <div className="bank-cards-scroll-container mb-3">
                          <div className="bank-cards-wrapper">
                            {getBanksData().map((bank) => (
                              <div className="bank-card-col" key={bank.id}>
                                <div
                                  onClick={() => setSelectedBank(bank.name)}
                                  className={`bank-card ${selectedBank === bank.name ? "selected" : ""}`}
                                  style={selectedBank === bank.name && bank.bankColorCode ? {
                                    borderColor: bank.bankColorCode,
                                  } : {}}
                                >
                                  {selectedBank === bank.name && (
                                    <div className="bank-card-gradient-top" />
                                  )}
                                  <div className="text-center">
                                    <div className="mb-2 bank-icon-container">
                                      <img
                                        src={bank.logo}
                                        alt={bank.name}
                                        className="bank-logo-img"
                                        onError={(e) => {
                                          e.target.src = "/images/bank.png";
                                        }}
                                      />
                                    </div>
                                    <p className={`mb-0 small bank-name ${selectedBank === bank.name ? "selected" : ""}`}>
                                      {bank.name}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Debit Card Offers */}
                        {selectedBank && getCardOffersByBank(selectedBank).length > 0 && (
                          <div className="card-offers-scroll-container mt-3">
                            <div className="card-offers-wrapper">
                              {getCardOffersByBank(selectedBank).map((card) => (
                                <div
                                  key={card.id || card.bin}
                                  onClick={() => setSelectedCardOffer(card)}
                                  className={`card-offer-item ${selectedCardOffer?.bin === card.bin ? "selected" : ""}`}
                                >
                                  {selectedCardOffer?.bin === card.bin && (
                                    <div className="bank-card-gradient-top" />
                                  )}
                                  <div className="d-flex justify-content-between align-items-center">
                                    <div className="flex-grow-1">
                                      <p className={`mb-1 small card-offer-name ${selectedCardOffer?.bin === card.bin ? "selected" : ""}`}>
                                        {card.name}
                                      </p>
                                      <p className="mb-0 card-offer-details">
                                        BIN: {card.bin} | Cap: Rs. {Number.parseInt(card.cap, 10).toLocaleString()} | {card.discount}% OFF
                                      </p>
                                    </div>
                                    <button
                                      className="btn btn-sm ms-2 card-offer-discount-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedCardOffer(card);
                                      }}
                                    >
                                      {card.discount}% OFF
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Payment Methods */}
                      <div className="mt-3">
                        <span className="h6 mb-2 d-block">Payment method</span>
                        <div className="row row-cols-3 g-2">
                          {clubAccounts.map((item, index) => (
                            <div className="col" key={index}>
                              <div
                                className={`card h-100 p-2 ${selectedPaymentTypeId === item.paymentTypeId
                                  ? "border-primary"
                                  : "border-white-50"
                                  }`}
                                onClick={() =>
                                  handlePaymentSelect(item.paymentTypeId)
                                }
                                style={{ cursor: "pointer" }}
                              >
                                <div className="position-relative">
                                  {selectedPaymentTypeId ===
                                    item.paymentTypeId && (
                                      <div className="position-absolute top-0 start-0 m-1">
                                        <div className="bg-primary rounded-circle p-1">
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="12"
                                            height="12"
                                            fill="white"
                                            className="bi bi-check"
                                            viewBox="0 0 16 16"
                                          >
                                            <path d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425a.267.267 0 0 1 .02-.022z" />
                                          </svg>
                                        </div>
                                      </div>
                                    )}
                                  <div className="text-center">
                                    <img
                                      src={
                                        item.paymentTypeId ===
                                          PaymentTypeEnum.Pay_On_Club
                                          ? "/images/icons/cash.png"
                                          : item.paymentTypeId ===
                                            PaymentTypeEnum.Payment_GateWay
                                            ? "/images/icons/payfast.png"
                                            : item.paymentTypeId ===
                                              PaymentTypeEnum.Online_Partial_Pay
                                              ? "/images/icons/icon_payment.png"
                                              : "/images/icons/payfast.png"
                                      }
                                      alt={item.display}
                                      className="img-fluid mb-1"
                                      style={{ maxHeight: "30px" }}
                                    />
                                    <p className="mb-0 small">{item.display}</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="col-md-12 mt-4">
                          <button
                            className="btn btn-primary w-100"
                            onClick={() => handleProceedBooking()}
                            disabled={
                              isProcessingCheckout ||
                              !selectedPaymentTypeId ||
                              isBookingInProgress
                            }
                          >
                            {isBookingInProgress
                              ? "Processing Booking..."
                              : "Book Now"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add the modal component */}
      <TermsAndConditionsModal
        show={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        terms={promoCodeDetails?.termsAndConditions}
      />

      {/* Add the booking terms modal */}
      <BookingTermsModal
        show={showBookingTermsModal}
        onClose={() => setShowBookingTermsModal(false)}
      />

      {/* Login Modal for Booking Flow */}
      {showLoginModal && (
        <LoginModal
          open={showLoginModal}
          onClose={handleLoginModalClose}
          onLoginSuccess={handleLoginSuccess}
        />
      )}

      {/* Booking Success Modal */}
      <BookingSuccessModal
        show={showBookingSuccessModal}
        onClose={() => {
          setShowBookingSuccessModal(false);
          setBookingSuccessData(null);
        }}
        bookingData={bookingSuccessData}
      />

      {/* Padel Loader */}
      <PadelLoader show={showPadelLoader} message={loaderMessage} />
      <div className="cs_height_120 cs_height_lg_80" />
    </>
  );
};

export default SportBooking;
